import { Portal } from '@radix-ui/react-portal';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Input } from '../Input';

export type SuggestionActionOption = {
  label: React.ReactNode;
  value: string;
  onSelect?: (value: string) => void;
};

export type SuggestionActionInputProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value' | 'onSubmit'> & {
  /** List of options with optional per-option onSelect. Filtered by case-insensitive substring match. */
  options: SuggestionActionOption[];
  /** Current value (controlled). Cleared after an option is selected or custom value is submitted. */
  value?: string;
  /** Called when the value changes (e.g. while typing). Cleared to '' after selection/submit. */
  onChange?: (value: string) => void;
  /** Fired when the user submits a custom/typed value (selects the custom option or presses Enter with typed text). */
  onSubmit?: (value: string) => void;
  /** Label for the custom option shown when the user has typed text. Receives the trimmed input. If omitted, no custom option row is shown (Enter still fires onSubmit when provided). */
  customOptionLabel?: (value: string) => React.ReactNode;
  /** Placeholder shown in the input */
  placeholder?: string;
  /** Maximum number of suggestion options to display (excluding the custom option). Default is options length */
  maxSuggestions?: number;
  /** Text shown when no suggestions match and there is no typed value for the custom option */
  emptyText?: string;
  /** Portal target for the list, defaulting to document.body. Must be positioned or transformed, such as DialogContent, because the list is absolutely positioned inside it. */
  container?: HTMLElement | null;
  className?: string;
};

const CUSTOM_OPTION_ID = 'suggestion-action-custom';

function SuggestionActionInput({
  options,
  value,
  onChange,
  onSubmit,
  customOptionLabel,
  placeholder,
  maxSuggestions = options.length,
  emptyText = 'No matching suggestions',
  container,
  className,
  onFocus,
  onBlur,
  onKeyDown,
  ...inputProps
}: SuggestionActionInputProps) {
  const [internalValue, setInternalValue] = React.useState(value ?? '');
  const [isOpen, setIsOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const [position, setPosition] = React.useState<{ top: number; left: number; width: number } | null>(null);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const positionTickRef = React.useRef<number | null>(null);

  const isControlled = value !== undefined;
  const displayValue = isControlled ? value : internalValue;

  React.useEffect(() => {
    if (isControlled && value !== undefined) setInternalValue(value);
  }, [isControlled, value]);

  const filteredOptions = React.useMemo(() => {
    const query = displayValue.trim().toLowerCase();
    if (!query) return options;
    return options.filter(
      (opt) =>
        opt.value.toLowerCase().includes(query) ||
        (typeof opt.label === 'string' && opt.label.toLowerCase().includes(query)),
    );
  }, [options, displayValue]);

  const visibleOptions = filteredOptions.slice(0, maxSuggestions);
  const trimmedInput = displayValue.trim();
  const showCustomOption = trimmedInput.length > 0 && typeof onSubmit === 'function' && customOptionLabel != null;
  const customOptionContent = showCustomOption ? customOptionLabel(trimmedInput) : null;

  const totalOptions = visibleOptions.length + (showCustomOption ? 1 : 0);
  const hasSuggestions = visibleOptions.length > 0;
  const showDropdown = isOpen && (hasSuggestions || showCustomOption || trimmedInput.length > 0);

  const updateValue = React.useCallback(
    (newValue: string) => {
      if (!isControlled) setInternalValue(newValue);
      onChange?.(newValue);
    },
    [isControlled, onChange],
  );

  const clearInput = React.useCallback(() => {
    updateValue('');
    setHighlightedIndex(0);
  }, [updateValue]);

  const selectSuggestionOption = React.useCallback(
    (option: SuggestionActionOption) => {
      option.onSelect?.(option.value);
      clearInput();
    },
    [clearInput],
  );

  const submitCustomValue = React.useCallback(
    (val: string) => {
      const toSubmit = val.trim();
      if (toSubmit) onSubmit?.(toSubmit);
      clearInput();
    },
    [onSubmit, clearInput],
  );

  const measurePosition = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (!container) return { top: rect.bottom, left: rect.left, width: rect.width };
    const containerRect = container.getBoundingClientRect();
    return {
      top: rect.bottom - containerRect.top - container.clientTop + container.scrollTop,
      left: rect.left - containerRect.left - container.clientLeft + container.scrollLeft,
      width: rect.width,
    };
  }, [container]);

  const updatePosition = React.useCallback(() => {
    const next = measurePosition();
    if (next) setPosition(next);
  }, [measurePosition]);

  // Keep dropdown aligned when input moves (e.g. dialog height change, scroll, resize).
  React.useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      if (positionTickRef.current != null) {
        cancelAnimationFrame(positionTickRef.current);
        positionTickRef.current = null;
      }
      return;
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    const tick = () => {
      const next = measurePosition();
      if (next) {
        setPosition((prev) => {
          if (prev && prev.top === next.top && prev.left === next.left && prev.width === next.width) return prev;
          return next;
        });
      }
      positionTickRef.current = requestAnimationFrame(tick);
    };
    positionTickRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      if (positionTickRef.current != null) {
        cancelAnimationFrame(positionTickRef.current);
        positionTickRef.current = null;
      }
    };
  }, [isOpen, updatePosition, measurePosition]);

  React.useEffect(() => {
    if (showDropdown && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, showDropdown]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateValue(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(0);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsOpen(true);
    setHighlightedIndex(0);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
    const nextFocus = (e.relatedTarget ?? document.activeElement) as Node | null;
    const isInsideInput = nextFocus && containerRef.current?.contains(nextFocus);
    const isInsideDropdown = nextFocus && listRef.current?.contains(nextFocus);
    if (isInsideInput || isInsideDropdown) return;
    setIsOpen(false);
    setHighlightedIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !showDropdown && trimmedInput && onSubmit) {
      e.preventDefault();
      submitCustomValue(displayValue);
      onKeyDown?.(e);
      return;
    }
    if (!showDropdown) {
      onKeyDown?.(e);
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => (i < totalOptions - 1 ? i + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => (i > 0 ? i - 1 : totalOptions - 1));
        break;
      case 'Enter': {
        e.preventDefault();
        if (highlightedIndex < visibleOptions.length) {
          const option = visibleOptions[highlightedIndex];
          if (option) selectSuggestionOption(option);
        } else if (showCustomOption && highlightedIndex === visibleOptions.length) {
          submitCustomValue(displayValue);
        }
        break;
      }
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(0);
        break;
      default:
        onKeyDown?.(e);
    }
  };

  const handleOptionMouseDown = (e: React.MouseEvent, option: SuggestionActionOption) => {
    e.preventDefault();
    selectSuggestionOption(option);
  };

  const handleCustomOptionMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    submitCustomValue(displayValue);
  };

  const portalContainer = container ?? document.body;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <Input
        ref={inputRef}
        {...inputProps}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls={showDropdown ? 'suggestion-action-input-list' : undefined}
        aria-activedescendant={
          showDropdown
            ? highlightedIndex < visibleOptions.length
              ? `suggestion-action-${highlightedIndex}`
              : showCustomOption
                ? CUSTOM_OPTION_ID
                : undefined
            : undefined
        }
        className={cn(showDropdown && 'rounded-b-none border-blue-50')}
      />

      {showDropdown && position && (
        <Portal container={portalContainer}>
          <div
            id="suggestion-action-input-list"
            ref={listRef}
            role="listbox"
            className="z-50 max-h-[200px] overflow-auto border border-t-0 border-blue-50 rounded-b bg-white shadow-md dark:bg-white/30"
            style={{
              position: container ? 'absolute' : 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              minWidth: position.width,
              maxHeight: 200,
            }}
          >
            {visibleOptions.length > 0 &&
              visibleOptions.map((option, index) => (
                <div
                  key={option.value}
                  id={`suggestion-action-${index}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={index === highlightedIndex}
                  className={cn(
                    'relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-xs leading-[1.4] text-grey-80 outline-hidden select-none hover:bg-grey-10',
                    index === highlightedIndex && 'bg-grey-10',
                  )}
                  onMouseDown={(e) => handleOptionMouseDown(e, option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option.label}
                </div>
              ))}
            {showCustomOption && (
              <div
                id={CUSTOM_OPTION_ID}
                role="option"
                tabIndex={-1}
                aria-selected={highlightedIndex === visibleOptions.length}
                className={cn(
                  'relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-xs leading-[1.4] text-grey-80 outline-hidden select-none hover:bg-grey-10',
                  highlightedIndex === visibleOptions.length && 'bg-grey-10',
                )}
                onMouseDown={handleCustomOptionMouseDown}
                onMouseEnter={() => setHighlightedIndex(visibleOptions.length)}
              >
                {customOptionContent}
              </div>
            )}
            {!hasSuggestions && !showCustomOption && <li className="px-2 py-1.5 text-xs text-grey-50">{emptyText}</li>}
          </div>
        </Portal>
      )}
    </div>
  );
}

export { SuggestionActionInput };
